import React, { useState, useEffect, useCallback } from "react";
import { FileBarChart } from "lucide-react";
import { Card, Spinner, PageHeader, Badge } from "../../lib/ui";
import { useT } from "../../lib/i18n";
import { useToast } from "../../lib/toast";
import api from "../../lib/api";
import { rp, pct, PeriodPicker, BranchFilter, BenchmarkBadge } from "../../components/finance";

const Row = ({ label, value, bold, indent, tone, badge, auto }) => (
  <div className={`flex items-center justify-between py-2 ${bold ? "border-t border-line font-bold" : ""}`}>
    <span className={`${indent ? "pl-5 text-ink-soft" : "text-ink"} ${bold ? "!text-ink" : ""} text-sm`}>
      {label}
      {auto && <Badge tone="info" className="ml-2 !px-1.5 !py-0.5 !text-[10px]">AUTO</Badge>}
      {badge}
    </span>
    <span className={`text-sm tabular-nums ${tone || (bold ? "text-ink" : "text-ink-soft")} ${bold ? "font-bold" : "font-medium"}`}>{rp(value)}</span>
  </div>
);

export default function ProfitLoss() {
  const { t } = useT();
  const toast = useToast();
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [branchId, setBranchId] = useState("");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/finance/profit-loss", { params: { period, branch_id: branchId || undefined } }); setD(data); }
    catch { toast.error(t("Failed to load")); }
    finally { setLoading(false); }
  }, [period, branchId, t, toast]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="finance-pnl-page">
      <PageHeader title={t("Profit & Loss")} subtitle={t("Income statement — auto from operations + manual entries")} icon={FileBarChart}>
        <BranchFilter value={branchId} onChange={setBranchId} />
        <PeriodPicker value={period} onChange={setPeriod} />
      </PageHeader>

      {loading || !d ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
        <div className="grid gap-5 lg:grid-cols-3">
          {/* Statement */}
          <Card className="lg:col-span-2" data-testid="finance-pnl-statement">
            <h3 className="mb-2 font-heading font-bold text-ink">{t("Income Statement")}</h3>
            {d.branch_name && <p className="-mt-1 mb-2 text-xs text-ink-mute" data-testid="pnl-branch-context">{t("Branch")}: <span className="font-semibold text-ink-soft">{d.branch_name}</span> · {pct(d.revenue_share_pct)} {t("of revenue")}</p>}
            <Row label={t("Revenue")} value={d.revenue} auto />
            <Row label={t("COGS / Food Cost")} value={-d.cogs} indent auto badge={<Badge tone="mute" className="ml-2 !px-1.5 !py-0.5 !text-[10px]">{t("cov")} {pct(d.cogs_coverage_pct)}</Badge>} />
            <Row label={t("Gross Profit")} value={d.gross_profit} bold tone={d.gross_profit >= 0 ? "text-success-text" : "text-danger-text"} />
            <Row label={t("Labor")} value={-d.labor} indent />
            <Row label={t("Occupancy")} value={-d.occupancy} indent />
            <Row label={t("Marketing")} value={-d.marketing} indent />
            <Row label={t("Other OPEX")} value={-d.other_opex} indent />
            <Row label={t("EBITDA")} value={d.ebitda} bold tone={d.ebitda >= 0 ? "text-success-text" : "text-danger-text"} />
            <Row label={t("Non-Operating (Tax/Interest/Depr.)")} value={-d.non_operating} indent />
            <Row label={t("Net Profit")} value={d.net_profit} bold tone={d.net_profit >= 0 ? "text-success-text" : "text-danger-text"} />
          </Card>

          {/* KPIs vs benchmark */}
          <Card data-testid="finance-pnl-kpis">
            <h3 className="mb-3 font-heading font-bold text-ink">{t("KPIs vs Benchmark")}</h3>
            <div className="space-y-2.5">
              {Object.entries(d.benchmarks).map(([key, b]) => (
                <div key={key} className="flex items-center justify-between border-b border-line/60 pb-2 last:border-0">
                  <div>
                    <p className="text-xs font-medium capitalize text-ink">{t(key.replace(/_/g, " ").replace(" pct", " %"))}</p>
                    <p className="text-[10px] text-ink-mute">{t("target")} {b.range[0]}–{b.range[1]}%</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-ink">{pct(b.value)}</span>
                    <BenchmarkBadge status={b.status} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-xl bg-stone-50 px-3 py-2 text-xs text-ink-soft">
              <p>{t("Prime Cost")}: <b className="text-ink">{rp(d.prime_cost)}</b> ({pct(d.kpis.prime_cost_pct)})</p>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
